import { useEffect, useRef } from 'react';

import type { GMetrixTableCellProps, GMetrixTableColumnProps } from 'components/GMetrixTable/components';
import { CustomTestsSearchContext } from 'pages/custom_tests/components/custom_tests_search/context';
import type { AssignWizardHandle } from 'pages/custom_tests/components/assign_wizard/AssignWizard';
import { useCustomTestArchiveDialogs } from 'pages/custom_tests/hooks/useCustomTestArchiveDialogs';
import type { DropdownMenuItem } from 'components/GMetrixButtonDropdown/GMetrixButtonDropdown';
import type { TableFilter } from 'components/GMetrixTable/components/toolbar/components';
import { AssignWizard } from 'pages/custom_tests/components/assign_wizard/AssignWizard';
import { CustomTestApi, CustomTestPagedItem } from 'apis/CustomTestApi';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Dialog, DialogHandle } from 'components/modal_manager';
import { useCustomTests } from 'pages/custom_tests/context';
import type { SortDescriptor } from 'utils/dataQuery';
import { helpContent } from 'pages/custom_tests/help';
import { Link, useNavigate } from 'react-router-dom';
import { useSuperState, useUrlFilters } from 'hooks';
import { useNotification } from 'app/notifications';
import { PageHeader } from 'components/layout/page';
import { currentLookupUrl } from 'utils/other';
import { Badge, Card } from 'react-bootstrap';
import { GMetrixTable } from 'components';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import { useApp } from 'app';

type CustomTestRow = CustomTestPagedItem;

export type CustomTestsSearchContextValue = {
    fetchTests: () => Promise<void>;
    handleAssign: (testId: number, testName: string, modeId?: number) => Promise<void>;
    loading: boolean;
    tests: CustomTestPagedItem[];
};

export const CustomTestsSearch = () => {

    // Persist the view (search, filters, sort, pagination) in the URL query string so navigating
    // into a test and back restores the same view — then refetch fresh rows for it. Keys match
    // the other lookup pages (skip/take), even though the API itself is page-based.
    const urlFilters = useUrlFilters({
        archived: false,
        mine: false,
        searchterm: '',
        skip: 0,
        sortdir: 'desc',
        sortfield: 'DateCreate',
        take: 10
    });

    const [state, setState] = useSuperState({
        debouncedSearchTerm: urlFilters.initial.searchterm,
        loading: true,
        onlyMine: urlFilters.initial.mine,
        page: Math.floor(urlFilters.initial.skip / urlFilters.initial.take) + 1,
        pageSize: urlFilters.initial.take,
        searchTerm: urlFilters.initial.searchterm,
        showArchived: urlFilters.initial.archived,
        sortDir: urlFilters.initial.sortdir as 'asc' | 'desc',
        sortField: urlFilters.initial.sortfield,
        tests: [] as CustomTestPagedItem[],
        totalCount: 0
    });
    const { debouncedSearchTerm, loading, onlyMine, page, pageSize, searchTerm, showArchived, sortDir, sortField,
        tests, totalCount } = state;

    const { currentUser } = useApp();
    const { formatMessage } = useIntl();
    const { permissions } = useCustomTests();
    const { createToast } = useNotification();
    const navigate = useNavigate();

    const assignWizardRef = useRef<AssignWizardHandle>(null);
    const dialogRef = useRef<DialogHandle>();
    const fetchSeqRef = useRef(0);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();

    const { confirmArchive, confirmRestore } = useCustomTestArchiveDialogs(dialogRef);

    const getModeLabel = (modeId: number) => {

        switch (modeId) {

            case 1:
                return formatMessage({ id: 'training.1fce7301', defaultMessage: 'Training' });
            case 3:
                return formatMessage({ id: 'both.8074bfd4', defaultMessage: 'Both' });
            default:
                return formatMessage({ id: 'testing.a439953c', defaultMessage: 'Testing' });

        }

    };

    const fetchTests = async () => {

        // Sequence guard: only the latest in-flight request may commit its result, so a
        // slow earlier response can never overwrite a newer page/filter's rows.
        const seq = ++fetchSeqRef.current;

        try {

            setState({ loading: true });

            const result = await CustomTestApi.SearchPaged({
                includeArchived: showArchived,
                onlyMine,
                page,
                pageSize,
                searchTerm: debouncedSearchTerm.trim(),
                sortDir,
                sortField
            });

            if (seq !== fetchSeqRef.current) return;

            setState({
                loading: false,
                tests: result?.Items ?? [],
                totalCount: result?.TotalCount ?? 0
            });

            urlFilters.write({
                archived: showArchived,
                mine: onlyMine,
                searchterm: debouncedSearchTerm,
                skip: (page - 1) * pageSize,
                sortdir: sortDir,
                sortfield: sortField,
                take: pageSize
            });

        } catch (e: any) {

            if (seq !== fetchSeqRef.current) return;

            setState({ loading: false });
            createToast(e?.message ?? (formatMessage({ id: 'failed_to_load_tests.ef0018f2', defaultMessage: 'Failed to load tests.' })), 'danger');

        }

    };

    const showArchiveConfirmation = (test: CustomTestRow) => confirmArchive({
        onSuccess: fetchTests,
        successMessage: formatMessage({ id: 'name_archived.099db7c3', defaultMessage: '"{name}" archived.' }, { name: test.Name }),
        testId: test.Id
    });

    const showRestoreConfirmation = (test: CustomTestRow) => confirmRestore({
        onSuccess: fetchTests,
        successMessage: formatMessage({ id: 'name_restored.1a3dfe1e', defaultMessage: '"{name}" restored.' }, { name: test.Name }),
        testId: test.Id
    });

    const handleAssign = async (testId: number, testName: string, modeId?: number) => {

        const wizard = assignWizardRef?.current;
        if (!wizard) return;

        const assigned = await wizard.show({ modeId, testId, testName });

        if (assigned) {

            createToast(
                formatMessage({ id: 'assignments_saved_successfully.22d8fe61', defaultMessage: 'Assignments saved successfully.' }),
                'success',
                5000
            );

            fetchTests();

        }

    };

    const handleCopyConfirmation = (test: CustomTestRow) => {

        const dialog = dialogRef?.current;
        if (!dialog) return;

        dialog.show({
            actions: [
                dialog.CancelAction(),
                dialog.OKAction({
                    label: formatMessage({ id: 'copy_and_edit.9b9cf664', defaultMessage: 'Copy and Edit' }),
                    onClick: () => navigate(`copy/${test.Id}`, { state: { from: currentLookupUrl() } })
                })
            ],
            body: (
                <p className={'mb-0'}>
                    <FormattedMessage defaultMessage='A copy of "{name}" will be created that you own. You can edit the copy without affecting the original test.' id="a_copy_of_name_will_be_created_that_you.989f1ef1" values={{ name: test.Name }} />
                </p>
            ),
            title: formatMessage({ id: 'copy_this_test.db81d881', defaultMessage: 'Copy this test?' })
        });

    };

    const handleExport = async () => {

        try {

            const result = await CustomTestApi.SearchPaged({
                includeArchived: showArchived,
                onlyMine,
                page: 1,
                pageSize: 10000,
                searchTerm: debouncedSearchTerm.trim(),
                sortDir,
                sortField
            });

            const rows = (result?.Items ?? []).map(item => {

                const row: Record<string, string | number> = {};
                row[formatMessage({ id: 'test_name.77ea159f', defaultMessage: 'Test Name' })] = item.Name;

                // StringProduct carries a trailing comma for multi-program tests —
                // normalize it the same way ProgramCell does.
                row[formatMessage({ id: 'programs.d867e0d6', defaultMessage: 'Program(s)' })] = (item.StringProduct ?? '')
                    .split(',')
                    .map(s => s.trim())
                    .filter(Boolean)
                    .join(', ');
                row[formatMessage({ id: 'created_by.a458757d', defaultMessage: 'Created By' })] = item.OwnerId === currentUser?.userProfileId
                    ? (formatMessage({ id: 'me.13543853', defaultMessage: 'Me' }))
                    : item.CreatedBy;
                row[formatMessage({ id: 'date_created.85efe6f3', defaultMessage: 'Date Created' })] = item.Date_create
                    ? new Date(item.Date_create).toLocaleDateString()
                    : '';
                row[formatMessage({ id: 'questions.940c8cce', defaultMessage: 'Questions' })] = item.NumQuestions;
                row[formatMessage({ id: 'assignments.f2dfa1bf', defaultMessage: 'Assignments' })] = item.AssignmentCount;
                row[formatMessage({ id: 'mode.f65b1f2b', defaultMessage: 'Mode' })] = getModeLabel(item.ModeId);
                row[formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' })] = item.IsActive
                    ? (formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' }))
                    : (formatMessage({ id: 'archived.e57c14b9', defaultMessage: 'Archived' }));
                return row;

            });

            const csv = Papa.unparse(rows);
            saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), 'custom-tests.csv');

        } catch (e: any) {

            createToast(e?.message ?? (formatMessage({ id: 'failed_to_export_tests.fcef38b1', defaultMessage: 'Failed to export tests.' })), 'danger');

        }

    };

    const handleServerPageChange = (skip: number, take: number) => setState({
        page: Math.floor(skip / take) + 1,
        pageSize: take
    });

    const handleServerSortChange = (sort: SortDescriptor[]) => {

        const next = sort[0];

        setState({
            page: 1,
            sortDir: next?.dir ?? 'desc',
            sortField: next?.field ?? 'DateCreate'
        });

    };

    useEffect(() => {

        fetchTests();

    }, [page, pageSize, sortField, sortDir, onlyMine, showArchived, debouncedSearchTerm]);

    useEffect(() => {

        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        if (searchTerm === debouncedSearchTerm) return;

        searchDebounceRef.current = setTimeout(() => setState({ debouncedSearchTerm: searchTerm, page: 1 }), 400);

        return () => {

            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

        };

    }, [searchTerm]);

    const context: CustomTestsSearchContextValue = {
        fetchTests,
        handleAssign,
        loading,
        tests
    };

    const tableFilters: TableFilter[] = [
        {
            checked: onlyMine,
            label: formatMessage({ id: 'only_my_tests.416783c6', defaultMessage: 'Only My Tests' }),
            onSelect: checked => setState({ onlyMine: checked, page: 1 }),
            type: 'checkbox'
        },
        {
            checked: showArchived,
            label: formatMessage({ id: 'archived_tests.29fd142d', defaultMessage: 'Archived Tests' }),
            onSelect: checked => setState({ page: 1, showArchived: checked }),
            type: 'checkbox'
        }
    ];

    const NameCell = (cellProps: GMetrixTableCellProps<CustomTestRow>) => (
        <td>
            <div className={'d-flex align-items-center gap-2'}>
                <span>
                    {cellProps.dataItem.Name}
                </span>
                {!cellProps.dataItem.IsActive && (
                    <Badge bg={'secondary'}>
                        <FormattedMessage defaultMessage="Archived" id="archived.e57c14b9" />
                    </Badge>
                )}
            </div>
        </td>
    );

    const ProgramCell = (cellProps: GMetrixTableCellProps<CustomTestRow>) => {

        const programs = (cellProps.dataItem.StringProduct ?? '')
            .split(',')
            .map(program => program.trim())
            .filter(program => program.length > 0)
            .sort((a, b) => a.localeCompare(b));

        return (
            <td title={programs.length > 1 ? programs.join(', ') : undefined}>
                {programs.length === 0 ?
                    (
                        <span className={'text-muted'}>
                            &mdash;
                        </span>
                    )
                    :
                    (
                        <div className={'d-flex align-items-center gap-2'}>
                            <span>
                                {programs[0]}
                            </span>
                            {programs.length > 1 && (
                                <span className={'text-primary text-decoration-underline'}>
                                    {`+${programs.length - 1}`}
                                </span>
                            )}
                        </div>
                    )}
            </td>
        );

    };

    const CreatedByCell = (cellProps: GMetrixTableCellProps<CustomTestRow>) => (
        <td>
            {cellProps.dataItem.OwnerId === currentUser?.userProfileId
                ? (formatMessage({ id: 'me.13543853', defaultMessage: 'Me' }))
                : cellProps.dataItem.CreatedBy}
        </td>
    );

    const DateCell = (cellProps: GMetrixTableCellProps<CustomTestRow>) => (
        <td>
            <FormattedDate value={new Date(cellProps.dataItem.Date_create)} />
        </td>
    );

    const buildDetailsDropdownItems = (dataItem: CustomTestRow): DropdownMenuItem[] => {

        const isOwner = dataItem.OwnerId === currentUser?.userProfileId;

        return [
            {
                action: () => handleCopyConfirmation(dataItem),
                hidden: !permissions.CanClone,
                text: formatMessage({ id: 'copy.fa37e351', defaultMessage: 'Copy' })
            },
            {
                action: () => handleAssign(dataItem.Id, dataItem.Name, dataItem.ModeId),
                hidden: !permissions.CanAssign || !isOwner || !dataItem.IsActive,
                text: formatMessage({ id: 'assign.0dabb6f2', defaultMessage: 'Assign' })
            },
            {
                action: () => (dataItem.IsActive ? showArchiveConfirmation(dataItem) : showRestoreConfirmation(dataItem)),
                hidden: !permissions.CanDelete || !isOwner,
                text: dataItem.IsActive ? (formatMessage({ id: 'archive.61ce1f3a', defaultMessage: 'Archive' })) : (formatMessage({ id: 'restore.d528bbad', defaultMessage: 'Restore' }))
            }
        ];

    };

    const noDataMessage = (
        <div className={'d-flex flex-column align-items-center justify-content-center py-5 text-center'}>
            <i className={'fa-light fa-circle-exclamation fs-1 text-muted mb-3'} />
            <h5 className={'text-muted mb-1'}>
                <FormattedMessage defaultMessage="No Custom Tests Found" id="no_custom_tests_found.ef432755" />
            </h5>
            <div className={'text-muted'}>
                <FormattedMessage defaultMessage="Try adjusting your search input or filters" id="try_adjusting_your_search_input_or_filte.6b3ba4f2" />
            </div>
        </div>
    );

    const extraToolBarButtons = (
        <GMetrixButton
            disabled={loading}
            onClick={handleExport}
            size={'sm'}
            variant={'outline-secondary'}
        >
            <i className={'fa-solid fa-file-export me-1'} />
            <FormattedMessage defaultMessage="Export" id="export.6f7a465c" />
        </GMetrixButton>
    );

    const columns: GMetrixTableColumnProps<CustomTestRow>[] = [
        { cell: NameCell, field: 'Name', sortField: 'TestName', title: formatMessage({ id: 'test_name.77ea159f', defaultMessage: 'Test Name' }) },
        { cell: ProgramCell, field: 'StringProduct', sortField: 'ProductNames', title: formatMessage({ id: 'program.a0ab1d4c', defaultMessage: 'Program' }) },
        { cell: CreatedByCell, field: 'CreatedBy', sortField: 'CreatedBy', title: formatMessage({ id: 'created_by.a458757d', defaultMessage: 'Created By' }) },
        { cell: DateCell, field: 'Date_create', sortField: 'DateCreate', title: formatMessage({ id: 'date_created.85efe6f3', defaultMessage: 'Date Created' }) },
        { field: 'NumQuestions', sortField: 'TotalQuestions', title: formatMessage({ id: 'questions.940c8cce', defaultMessage: 'Questions' }) },
        { field: 'AssignmentCount', sortField: 'AssignmentCount', title: formatMessage({ id: 'assignments.f2dfa1bf', defaultMessage: 'Assignments' }) }
    ];

    return (
        <CustomTestsSearchContext.Provider value={context}>
            <div className={'custom-tests-search d-flex flex-column gap-3'}>

                <div className={'d-flex justify-content-end'}>
                    <Link className={'btn btn-sm btn-outline-secondary'} to={'/custom-tests'}>
                        <i className={'fa-solid fa-arrow-right-arrow-left me-1'} />
                        <FormattedMessage defaultMessage="Switch to classic view" id="switch_to_classic_view.823eb2a4" />
                    </Link>
                </div>

                <PageHeader
                    helpContent={helpContent}
                    title={formatMessage({ id: 'custom_tests.9e7494ea', defaultMessage: 'Custom Tests' })}
                    {...(permissions.CanCreate ? {
                        cta: {
                            label: (
                                <div className={'d-inline-flex align-items-center gap-1'}>
                                    <i className={'fa-solid fa-plus'} />
                                    <span>
                                        <FormattedMessage defaultMessage="New Custom Test" id="new_custom_test.9df0dde0" />
                                    </span>
                                </div>
                            ),
                            to: 'create'
                        }
                    } : {})}
                />

                <Card body>
                    <GMetrixTable<CustomTestRow>
                        inlineColumnToggle
                        serverSidePagination
                        toggleColumns
                        actionsHeaderLabel={''}
                        columnData={columns}
                        defaultItemsPerPage={pageSize}
                        detailsDropdownItems={buildDetailsDropdownItems}
                        extraToolBarButtons={extraToolBarButtons}
                        filters={tableFilters}
                        initialSkip={(page - 1) * pageSize}
                        loading={loading}
                        noDataMessage={noDataMessage}
                        onDetailsItemClick={dataItem => navigate(`edit/${dataItem.Id}`, { state: { from: currentLookupUrl() } })}
                        onServerPageChange={handleServerPageChange}
                        onServerSortChange={handleServerSortChange}
                        resetPageSignal={`${debouncedSearchTerm}|${onlyMine}|${showArchived}`}
                        rowData={tests}
                        searchPlaceholder={formatMessage({ id: 'search.0ab06b2c', defaultMessage: 'Search' })}
                        searchTerm={searchTerm}
                        serverTotalCount={totalCount}
                        setSearchTerm={value => setState({ searchTerm: value })}
                    />
                </Card>

                <Dialog ref={dialogRef} />

                <AssignWizard ref={assignWizardRef} />

            </div>
        </CustomTestsSearchContext.Provider>
    );

};
